// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import {
  Button,
  Caption1,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Lightbulb24Regular } from "@fluentui/react-icons";
import {
  TeachingPopover,
  TeachingPopoverBody,
  TeachingPopoverCarousel,
  TeachingPopoverCarouselCard,
  TeachingPopoverHeader,
  TeachingPopoverSurface,
  TeachingPopoverTitle,
  TeachingPopoverTrigger,
} from "@fluentui/react-teaching-popover";
import type { TourStep } from "../dto/tourModels";

const useStyles = makeStyles({
  body: {
    display: "grid",
    gap: tokens.spacingVerticalS,
    minWidth: "260px",
    maxWidth: "340px",
  },
  hint: {
    display: "block",
    color: tokens.colorNeutralForeground3,
  },
  learnCol: {
    display: "grid",
    gap: tokens.spacingVerticalXS,
    justifyContent: "center",
    marginTop: tokens.spacingVerticalM,
  },
  footer: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    marginTop: tokens.spacingVerticalM,
  },
  count: {
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
});

export function formatStepCount(current: number, total: number): string {
  return `${current + 1} of ${total}`;
}

export interface TutorialCarouselProps {
  open: boolean;
  target: HTMLElement | null;
  tourTitle: string;
  steps: readonly TourStep[];
  currentIndex: number;
  onSelectIndex: (index: number) => void;
  onFinish: () => void;
  onDismiss: () => void;
  detailOpen: boolean;
  detailSteps: readonly TourStep[];
  detailIndex: number;
  onOpenDetail: (index: number) => void;
  onSelectDetailIndex: (index: number) => void;
  onFinishDetail: () => void;
  onCloseDetail: () => void;
}

export function TutorialCarousel({
  open,
  target,
  tourTitle,
  steps,
  currentIndex,
  onSelectIndex,
  onFinish,
  onDismiss,
  detailOpen,
  detailSteps,
  detailIndex,
  onOpenDetail,
  onSelectDetailIndex,
  onFinishDetail,
  onCloseDetail,
}: TutorialCarouselProps) {
  const styles = useStyles();
  const currentStep = steps[currentIndex];

  if (!open || !currentStep || !target) {
    return null;
  }

  const activeSteps = detailOpen ? detailSteps : steps;
  const activeIndex = detailOpen ? detailIndex : currentIndex;
  const activeStep = activeSteps[activeIndex];

  if (!activeStep) {
    return null;
  }

  const isFirst = activeIndex === 0;
  const isFinal = detailOpen
    ? activeIndex === activeSteps.length - 1
    : currentStep.isFinal === true;
  const total = activeSteps.length;

  const handleValueChange = (value: string | undefined): void => {
    const nextIndex = activeSteps.findIndex((step) => step.id === value);
    if (nextIndex >= 0) {
      if (detailOpen) {
        onSelectDetailIndex(nextIndex);
      } else {
        onSelectIndex(nextIndex);
      }
    }
  };

  const advance = (): void => {
    if (detailOpen) {
      if (activeIndex === activeSteps.length - 1) {
        onFinishDetail();
      } else {
        onSelectDetailIndex(Math.min(activeIndex + 1, total - 1));
      }
    } else if (currentStep.isFinal) {
      onFinish();
    } else {
      onSelectIndex(Math.min(currentIndex + 1, steps.length - 1));
    }
  };

  const back = (): void => {
    if (detailOpen) {
      onSelectDetailIndex(Math.max(activeIndex - 1, 0));
    } else {
      onSelectIndex(Math.max(currentIndex - 1, 0));
    }
  };

  const primaryLabel = isFinal ? "Done" : "Next";

  return (
    <TeachingPopover
      open
      withArrow
      trapFocus
      positioning={{ target }}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          onDismiss();
        }
      }}
    >
      <TeachingPopoverTrigger disableButtonEnhancement>
        <span />
      </TeachingPopoverTrigger>
      <TeachingPopoverSurface>
        <TeachingPopoverHeader>{tourTitle}</TeachingPopoverHeader>
        <TeachingPopoverCarousel
          value={activeStep.id}
          onValueChange={(_event, data) => handleValueChange(data.value)}
          announcement={(value) => {
            const step = activeSteps.find((s) => s.id === value);
            return `${tourTitle} — ${step?.title ?? ""}`;
          }}
        >
          {activeSteps.map((step) => (
            <TeachingPopoverCarouselCard key={step.id} value={step.id}>
              <TeachingPopoverBody>
                <TeachingPopoverTitle>{step.title}</TeachingPopoverTitle>
                <div className={styles.body}>
                  <div>{step.body}</div>
                  {step.hint ? (
                    <Caption1 className={styles.hint}>{step.hint}</Caption1>
                  ) : null}
                </div>
              </TeachingPopoverBody>
            </TeachingPopoverCarouselCard>
          ))}
        </TeachingPopoverCarousel>
        {!detailOpen && currentStep.details && currentStep.details.length > 0 ? (
          <div className={styles.learnCol}>
            {currentStep.details.map((entry, index) => (
              <Button
                key={entry.trigger}
                appearance="subtle"
                icon={<Lightbulb24Regular />}
                onClick={() => onOpenDetail(index)}
              >
                {entry.trigger}
              </Button>
            ))}
          </div>
        ) : null}
        <div className={styles.footer}>
          {detailOpen && activeIndex === 0 ? (
            <Button appearance="subtle" onClick={onCloseDetail}>
              Back to tour
            </Button>
          ) : (
            <Button appearance="subtle" disabled={isFirst} onClick={back}>
              Back
            </Button>
          )}
          <Caption1 className={styles.count} aria-live="polite">
            {formatStepCount(activeIndex, total)}
          </Caption1>
          <Button appearance="primary" onClick={advance}>
            {primaryLabel}
          </Button>
        </div>
      </TeachingPopoverSurface>
    </TeachingPopover>
  );
}
